import { instance } from "api/apiBase";
import { nylasApi } from "api/nylas/api";
import {
  type Agent,
  type PersonalityPreset
} from "views/settings-users/slices/agents/types";
import { agentsPath } from "views/settings-users/slices/agents/constants";
import {
  type AgentEmail,
  type AgentTimeZone,
  type CallMode,
  type ElevenLabsRegistrationStatus,
  type LeasingAgentConfig,
  type MessageMode,
  type MessagingGoal,
  type ProvisionedPhoneNumberListResponse
} from "../components/types";

// The legacy portal/v1/agents/leasing_agents routes were removed in AI-251;
// wizard progress and personality live on the agent itself. A null
// personality_text clears it (only meaningful for the custom preset).
export const updateAgentWizardState = async (
  agentId: number,
  payload: {
    wizard_step?: number;
    wizard_status?: string;
    personality_preset?: PersonalityPreset;
    personality_text?: string | null;
  }
) => {
  const { data } = await instance.patch<Agent>(
    `${agentsPath}/${agentId}`,
    payload
  );
  return data;
};

export const PROVISIONED_PHONE_NUMBERS_KEY = ["PROVISIONED_PHONE_NUMBERS"];

export const fetchProvisionedPhoneNumbers = async () => {
  const { data } = await instance.get<ProvisionedPhoneNumberListResponse>(
    "portal/v1/provisioned_phone_numbers",
    // The assigned-rows list, override chips, pickers and "N phone numbers
    // assigned" count are all built from this one response, so it must hold the
    // partner's full set — a page cap silently hid every number past it.
    { params: { skip_pagination: true } }
  );
  return data;
};

export const AGENT_EMAILS_KEY = ["AGENT_EMAILS"];

export const fetchAgentEmails = async (): Promise<AgentEmail[]> => {
  const grants = await nylasApi.listGrants({ product: "mail" });
  // POST agents/configs only accepts shared mail grants — a personal grant is
  // rejected with a 422 that also rolls back any phone configs enrolled in the
  // same Save (Create wraps every target in one transaction). Never offer
  // personal mailboxes so the picker can't produce an unsaveable selection.
  // Filtering here means EmailSelect, EmailsStep and OverrideBar all inherit the
  // same eligibility rule.
  return grants
    .filter((grant) => grant.account_type === "shared")
    .map((grant) => ({
      id: grant.id,
      email: grant.email,
      provider: grant.provider,
      account_type: grant.account_type,
      assigned_properties: grant.assigned_properties ?? []
    }));
};

export const AGENT_MESSAGING_GOALS_KEY = ["AGENT_MESSAGING_GOALS"];

export const fetchAgentMessagingGoals = async (agentId: number) => {
  const { data } = await instance.get<MessagingGoal[]>(
    `${agentsPath}/${agentId}/messaging_goals`
  );
  return data;
};

// Replace-list semantics: the given goals become the agent's complete set.
export const replaceAgentMessagingGoals = async (
  agentId: number,
  goals: { stage_id: number; instruction: string; position: number }[]
) => {
  const { data } = await instance.put<MessagingGoal[]>(
    `${agentsPath}/${agentId}/messaging_goals`,
    { goals }
  );
  return data;
};

// No agent id in the path or key by design: a partner has at most one leasing
// agent (DB-enforced singleton; every agent type is singleInstance), and the
// backend resolves it implicitly from the authenticated partner. The endpoint
// is inherently partner-scoped, so there is no ambiguity about whose configs
// these are.
export const LEASING_AGENT_CONFIGS_KEY = ["LEASING_AGENT_CONFIGS"];

export const fetchLeasingAgentConfigs = async () => {
  const { data } = await instance.get<LeasingAgentConfig[]>(
    "portal/v1/agents/configs"
  );
  return data;
};

export const createLeasingAgentConfigs = async (payload: {
  provisioned_phone_number_ids?: number[];
  crm_nylas_grant_ids?: number[];
}) => {
  const { data } = await instance.post<LeasingAgentConfig[]>(
    "portal/v1/agents/configs",
    payload
  );
  return data;
};

export const deleteLeasingAgentConfig = async (configId: number) => {
  await instance.delete(`portal/v1/agents/configs/${configId}`);
};

// Re-runs the number's ElevenLabs registration server-side (import + assign
// for an active agent, unassign for an inactive one) and returns the fresh
// registration status.
export const retryConfigRegistration = async (configId: number) => {
  const { data } = await instance.post<{
    provisioned_phone_number_id: number;
    elevenlabs_registration_status: ElevenLabsRegistrationStatus;
    elevenlabs_last_synced_at: string | null;
    elevenlabs_last_error: string | null;
  }>(`portal/v1/agents/configs/${configId}/retry_registration`);
  return data;
};

// Explicit null re-inherits: on a member row it falls back to the default row,
// on the default row it falls back to the Boom-managed defaults. On a phone
// row, use_boom_default_greeting: true forces the Boom-managed greeting instead
// of inheriting the default row.
export const updateLeasingAgentConfig = async (
  configId: number,
  payload: {
    greeting_message_text?: string | null;
    custom_greeting_message_text?: string | null;
    use_boom_default_greeting?: boolean;
    working_days?: number[] | null;
    working_hours_start?: string | null;
    working_hours_end?: string | null;
    business_hours_message_mode?: MessageMode | null;
    after_hours_message_mode?: MessageMode | null;
    business_hours_call_mode?: CallMode;
    after_hours_call_mode?: CallMode;
    time_zone?: AgentTimeZone;
  }
) => {
  const { data } = await instance.patch<LeasingAgentConfig>(
    `portal/v1/agents/configs/${configId}`,
    payload
  );
  return data;
};
